/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */

import { FieldArray, useFormikContext } from 'formik';
import {
  DataPath,
  EnclosureGroup,
  HAN_TERMINAL_TYPE,
  ProvisionedTerminal,
  TerminalFormSchema,
} from '../../../shared/entities/provisionedTerminal';
import { AccordionDetails, AccordionSummary, Box, IconButton } from '@mui/material';
import { useMemo, useState } from 'react';
import { BeamInputLabel } from '../../common/formik/BeamInputLabel';
import { BeamAccordion } from '../../common/beam/BeamAccordion';
import ProvisioningAddButton from '../ProvisioningAddButton';
import { Delete, Warning } from '@mui/icons-material';
import appTheme from '../../common/theme/appTheme.json';
import ModalDialog from '../../common/dialogs/ModalDialog';
import { BeamMultiSelect } from '../../common/formik/BeamMultiSelect';
import { isNil } from 'lodash';

/**
 * Generates a unique identifier string for a modem in the Terminal Form based on the provided data path index,
 * data path label, and modem index. This string will be used as an option in the Enclosure Group's modem multiselect dropdown.
 *
 * @param datapathIdx - The index of the data path.
 * @param datapathLabel - The label of the data path.
 * @param modemIdx - The index of the modem.
 * @returns A string representing the unique identifier for the modem form e.g. Data Path #1: KA | Modem #1.
 */
const getModemFormIdentifier = (datapathIdx: number, datapathLabel: string | undefined, modemIdx: number): string => {
  if (datapathLabel === undefined) {
    return `Data Path #${datapathIdx + 1} | Modem #${modemIdx + 1}`;
  }
  return `Data Path #${datapathIdx + 1}: ${datapathLabel} | Modem #${modemIdx + 1}`;
};

/**
 * Enriches the data paths' Modems with an enclosure label based on the enclosure group inputs.
 * This function is called when the user submits the Terminal Form.
 *
 * @param terminaFormData - Values from the Terminal Form
 * @returns The entire provisioned terminal data with the Modems' enclosure labels enriched
 */
export const enrichEnclosureLabels = (terminalFormData: TerminalFormSchema): ProvisionedTerminal => {
  // Enclosure Groups data
  const enclosureGroups = terminalFormData.enclosureGroups;

  // Provisioned Terminal (format that is sent to and received from the backend)
  const provisionedTerminal: ProvisionedTerminal = {
    id: terminalFormData.id,
    displayName: terminalFormData.displayName,
    platformType: terminalFormData.platformType,
    terminalType: terminalFormData.terminalType,
    customerGroup: terminalFormData.customerGroup,
    serviceTier: terminalFormData.serviceTier,
    restrictedDefault: terminalFormData.restrictedDefault,
    tags: terminalFormData.tags,
    datapaths: terminalFormData.datapaths,
    contracts: terminalFormData.contracts,
    belongsToOrgs: terminalFormData.belongsToOrgs,
    // Only include the HAN Agent ID for HAN terminals; omitted from the payload otherwise
    hanAgentId:
      terminalFormData.terminalType === HAN_TERMINAL_TYPE && terminalFormData.hanAgentId?.trim()
        ? terminalFormData.hanAgentId.trim()
        : undefined,
    // Only include the HaloNet Space Vehicle Name for HaloNet terminals; omitted from the payload otherwise
    haloNetSpaceVehicleName:
      terminalFormData.terminalType === 'HaloNet' ? terminalFormData.haloNetSpaceVehicleName : undefined,
  };

  // Iterate through all data paths
  provisionedTerminal.datapaths.forEach((datapath, datapathIdx) => {
    // Iterate through all modems in the data path
    datapath.modems.forEach((modem, modemIdx) => {
      // Get the modem form identifier
      const modemFormIdentifier = getModemFormIdentifier(datapathIdx, datapath.label, modemIdx);

      // Find the enclosure label for the modem and add it to the modem object
      const enclosureLabel = findEnclosureLabelForModem(enclosureGroups, modemFormIdentifier);
      modem.enclosureLabel = enclosureLabel;
    });
  });

  return provisionedTerminal;
};

/**
 * Finds the enclosure label for the modem with the provided modem form identifier.
 *
 * @param enclosureGroups - Modem form identifiers for each enclosure group
 * @param modemFormIdentifier - The form identifier of the modem (e.g. "Data Path #1: KU | Modem #1")
 * @returns The enclosure label for the modem or null if it is not found
 */
const findEnclosureLabelForModem = (enclosureGroups: EnclosureGroup[], modemFormIdentifier: string): string | null => {
  for (let enclosureGroupIdx = 0; enclosureGroupIdx < enclosureGroups.length; enclosureGroupIdx++) {
    const enclosureGroup = enclosureGroups[enclosureGroupIdx];

    if (enclosureGroup.modemFormIdentifiers.includes(modemFormIdentifier)) {
      return enclosureGroup.label;
    }
  }

  return null;
};

/**
 * Converts the provided Provisioned Terminal to a Terminal Form Schema.
 *
 * @param provisionedTerminal - The provisioned terminal to convert
 * @returns The Terminal Form Schema
 */
export const convertProvisionedTerminalToTerminalFormSchema = (
  provisionedTerminal: ProvisionedTerminal,
): TerminalFormSchema => {
  return {
    ...provisionedTerminal,
    hanAgentId: provisionedTerminal.hanAgentId ?? '',
    haloNetSpaceVehicleName: provisionedTerminal.haloNetSpaceVehicleName ?? '',
    enclosureGroups: createEnclosureGroups(provisionedTerminal.datapaths),
  };
};

/**
 * Creates the Enclosure Groups based on the provided data paths.
 * @param datapaths - The data paths to create the enclosure groups from
 * @returns The Enclosure Groups field that formik uses to render the enclosure group inputs
 */
const createEnclosureGroups = (datapaths: DataPath[]): EnclosureGroup[] => {
  // Mapping of enclosure labels to modem form identifiers
  const enclosureGroupsMap: Map<string, string[]> = new Map();

  // Iterate through datapaths
  datapaths.forEach((datapath, datapathIdx) => {
    // Iterate through modems
    datapath.modems.forEach((modem, modemIdx) => {
      // Get the enclosure label for the modem
      const enclosureLabel: string | null = modem.enclosureLabel;

      if (enclosureLabel) {
        // Get the modem form identifier
        const modemFormIdentifier = getModemFormIdentifier(datapathIdx, datapath.label, modemIdx);

        // Add the modem form identifier to the enclosure group
        const modemFormIdentifierList = enclosureGroupsMap.get(enclosureLabel) ?? [];
        modemFormIdentifierList.push(modemFormIdentifier);
        enclosureGroupsMap.set(enclosureLabel, modemFormIdentifierList);
      }
    });
  });

  // Convert the map to an array of EnclosureGroup objects
  const enclosureGroups: EnclosureGroup[] = Array.from(enclosureGroupsMap.entries()).map(
    ([label, modemFormIdentifiers]) => {
      return { label, modemFormIdentifiers };
    },
  );

  return enclosureGroups;
};

/**
 * Gets the label for the enclosure group based on the index.
 * @param enclosureGroupIdx - The index of the enclosure group
 * @returns A string representing the label for the enclosure group
 */
const getEnclosureLabel = (enclosureGroupIdx: number) => {
  return `Enclosure Group #${enclosureGroupIdx + 1}`;
};

export const EnclosureGroups = () => {
  const {
    values: { datapaths, enclosureGroups, terminalType },
  } = useFormikContext<TerminalFormSchema>();

  // Array of all modem form identifiers for every configured modem in Terminal Form (Modem -> 'Data Path #X: <label> - Modem #Y')
  const modemFormIdentifiers: string[] = useMemo(() => {
    return (
      datapaths
        // Iterate through all data paths
        .map((datapath, datapathIdx) => {
          // Iterate through all modems in the data path and return the form identifier for each one (string[]), if an antenna is defined
          return datapath.antenna
            ? datapath.modems.map((_modem, modemIdx) => getModemFormIdentifier(datapathIdx, datapath.label, modemIdx))
            : [];
        })
        // Flatten the nested array of modem form identifiers
        .flat()
    );
  }, [datapaths]);

  // Map of all modem form identifiers that have been selected in any enclosure group and their enclosure group index
  const selectedModemFormIdentifiers: Map<string, number> = useMemo(() => {
    // String: Modem Form Identifier (e.g. "Data Path #1: KU | Modem #1"), Number: Enclosure Group Index
    const map = new Map<string, number>();

    // Iterate through all enclosure groups
    enclosureGroups.forEach((enclosureGroup, enclosureGroupIdx) => {
      enclosureGroup.modemFormIdentifiers.forEach(modemFormIdentifier => {
        map.set(modemFormIdentifier, enclosureGroupIdx);
      });
    });

    return map;
  }, [enclosureGroups]);

  // Don't display Enclosure Groups if it's not a HAN terminal
  if (isNil(terminalType) || terminalType !== HAN_TERMINAL_TYPE) return <></>;

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <BeamInputLabel label={'Enclosure Groups'} htmlFor={'enclosureGroups'} required={true} />
      <FieldArray name={'enclosureGroups'}>
        {({ remove, push }) => {
          return (
            <div>
              {enclosureGroups.length > 0 &&
                enclosureGroups.map((enclosureGroup, enclosureGroupIdx) => (
                  <EnclosureGroupBuilder
                    key={`enclosure-group-${enclosureGroupIdx}`}
                    formikEnclosureGroupLabel={enclosureGroup.label}
                    enclosureGroupIdx={enclosureGroupIdx}
                    modemOptions={modemFormIdentifiers}
                    selectedModemsMap={selectedModemFormIdentifiers}
                    deleteEnabled={true}
                    onDelete={() => {
                      remove(enclosureGroupIdx);
                    }}
                  />
                ))}
              <ProvisioningAddButton
                data-testid="add-enclosure-group"
                text={'Add Enclosure Group'}
                onClick={() => {
                  push({
                    label: getEnclosureLabel(enclosureGroups.length + 1),
                    modemFormIdentifiers: [],
                  });
                }}
              />
            </div>
          );
        }}
      </FieldArray>
    </Box>
  );
};

const EnclosureGroupBuilder = ({
  formikEnclosureGroupLabel,
  enclosureGroupIdx,
  modemOptions,
  selectedModemsMap,
  deleteEnabled,
  onDelete,
}: {
  formikEnclosureGroupLabel: string;
  enclosureGroupIdx: number;
  modemOptions: string[];
  selectedModemsMap: Map<string, number>;
  deleteEnabled: boolean;
  onDelete: (enclosureGroupIdx: number) => void;
}) => {
  // State to track if the delete modal should be shown
  const [showDeleteDataPathModal, setShowDeleteDataPathModal] = useState(false);

  const { setFieldValue } = useFormikContext<TerminalFormSchema>();

  // Determine the correct enclosure label to display (updates as the user removes other enclosure groups)
  const enclosureLabel = useMemo(() => {
    // Default to the enclosureLabel provided by Formik
    let finalizedEnclosureLabel = formikEnclosureGroupLabel;

    // If the enclosure label matches the default label template, ensure the correct index is displayed
    if (finalizedEnclosureLabel.match(/Enclosure Group #\d+/)) {
      finalizedEnclosureLabel = getEnclosureLabel(enclosureGroupIdx);

      // Update formik's enclosure group label to the finalized label
      setFieldValue(`enclosureGroups[${enclosureGroupIdx}].label`, finalizedEnclosureLabel);
    }

    // Finalized enclosure label is either the provided label from the backend or the default label with the correct index
    return finalizedEnclosureLabel;
  }, [formikEnclosureGroupLabel, enclosureGroupIdx, setFieldValue]);

  return (
    <>
      <BeamAccordion key={`enclosure-group-${enclosureGroupIdx}-accordion`} defaultExpanded>
        <AccordionSummary>
          {enclosureLabel}
          {deleteEnabled && (
            <IconButton
              data-testid={`delete-enclosure-group-${enclosureGroupIdx}`}
              onClick={event => {
                event.stopPropagation();
                setShowDeleteDataPathModal(true);
              }}
              sx={{ padding: 0, marginLeft: 'auto' }}
            >
              <Delete sx={{ color: appTheme.primaryTextColor, fontSize: 24 }} />
            </IconButton>
          )}
        </AccordionSummary>

        <AccordionDetails style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
          <BeamMultiSelect
            name={`enclosureGroups[${enclosureGroupIdx}].modemFormIdentifiers`}
            label={'Modems'}
            options={modemOptions || []}
            placeholder={'Select modems'}
            required={true}
            getOptionDisabled={(modemFormIdentifier: string) => {
              // Disable the option if the modem is already selected in a different enclosure group
              return (
                selectedModemsMap.has(modemFormIdentifier) &&
                selectedModemsMap.get(modemFormIdentifier) !== enclosureGroupIdx
              );
            }}
          />
        </AccordionDetails>
      </BeamAccordion>
      {deleteEnabled && (
        <ModalDialog
          open={showDeleteDataPathModal}
          onLeftBtnClicked={() => {
            setShowDeleteDataPathModal(false);
          }}
          onRightBtnClicked={() => {
            onDelete(enclosureGroupIdx);
            setShowDeleteDataPathModal(false);
          }}
          title={
            <Box display={'flex'} gap={'8px'} alignItems={'center'}>
              <Warning htmlColor={'#FFB029'} /> Confirm Enclosure Group Deletion
            </Box>
          }
          message={
            <>
              Are you sure you want to delete this enclosure group?
              <br />
              <br />
              This action cannot be undone.
            </>
          }
        />
      )}
    </>
  );
};
